import { Box, type SxProps, type Theme } from '@mui/material';
import {
  useRef,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type PropsWithChildren,
} from 'react';

import {
  useBoardDragPreviewStore,
  useWorkspaceUiStore,
  type BoardElementType,
  type WallBoard,
  type WallCard,
} from '@walls/store';
import { useBoardElementRect } from '../hooks/useBoardElementRect';
import { useCardDrag } from './hooks/useCardDrag';
import { useCardPermissions } from './hooks/useCardPermissions';
import { useCardResize } from './hooks/useCardResize';
import { useCardSelection } from './hooks/useCardSelection';
import type { CardResizeHandle } from './utils/cardResizeGeometry';
import { getBoardPositionStyles } from './utils/cardGeometry';
import { isDarkCard } from './utils/cardDisplay';
import { isCardVisibleForParticipantFilter } from './utils/cardVisibility';

interface CardFrameProps extends PropsWithChildren {
  board: WallBoard;
  card: WallCard;
  elementType?: BoardElementType;
  isClusterChild?: boolean;
  measureDependencies?: unknown[];
  sx?: SxProps<Theme>;
}

export function CardFrame({
  board,
  card,
  children,
  elementType = 'card',
  isClusterChild = false,
  measureDependencies = [],
  sx,
}: CardFrameProps) {
  const elementRef = useRef<HTMLElement | null>(null);
  const dragPreview = useBoardDragPreviewStore((state) => state.cardDragPreviews[card.id]);
  const visibleParticipantIds = useWorkspaceUiStore((state) => state.visibleParticipantIds);
  const permissions = useCardPermissions(board, card);
  const isFocusFiltered = !isCardVisibleForParticipantFilter(card, visibleParticipantIds);
  const canSelect = permissions.canSelect && !isFocusFiltered;
  const canMove = permissions.canMove && !isFocusFiltered;
  const canResize = permissions.canResize && !isFocusFiltered;
  const selection = useCardSelection({
    board,
    canEdit: permissions.canEditContent && !isFocusFiltered,
    canSelect,
    card,
    elementType,
  });
  const drag = useCardDrag({
    board,
    canMove,
    card,
    isClusterChild,
  });
  const resize = useCardResize({
    board,
    canResize,
    card,
    elementRef,
    isClusterChild,
  });
  const currentPosition = getBoardPositionStyles(card, isClusterChild);
  const dragTransform = dragPreview
    ? `translate3d(${dragPreview.deltaX}px, ${dragPreview.deltaY}px, 0)`
    : undefined;
  const cardSx = sx ? (Array.isArray(sx) ? sx : [sx]) : [];
  useBoardElementRect(card.id, elementRef, [
    card.xPos,
    card.yPos,
    card.width,
    card.height,
    card.content,
    card.title,
    isClusterChild,
    ...measureDependencies,
  ]);

  return (
    <Box
      ref={elementRef}
      id={`item-content-container_${card.id}`}
      data-can-edit={permissions.canEdit}
      data-can-move={canMove}
      data-can-resize={canResize}
      data-element-id={card.id}
      data-element-type={elementType}
      data-is-locked={card.isLocked}
      data-is-resized={Boolean(card.width || card.height)}
      onClick={(event: ReactMouseEvent<HTMLElement>) => {
        if (drag.shouldSuppressClick()) {
          event.preventDefault();
          event.stopPropagation();
        }
      }}
      onDoubleClick={(event: ReactMouseEvent<HTMLElement>) => {
        if (drag.shouldSuppressClick()) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }

        selection.onDoubleClick(event);
      }}
      onPointerDown={(event: ReactPointerEvent<HTMLElement>) => {
        if (resize.isResizing) {
          return;
        }

        selection.onPointerDown(event);
        drag.onPointerDown(event);
      }}
      onPointerMove={drag.onPointerMove}
      onPointerUp={drag.onPointerUp}
      onPointerCancel={drag.onPointerCancel}
      sx={[
        {
          boxShadow:
            selection.isCurrent || selection.isSelected
              ? '0 0 0 2px #7dc9fb'
              : card.isLocked
              ? 'none'
              : '0 1px 1px rgba(0, 0, 0, 0.15)',
          color: isDarkCard(card.color) ? '#fff' : '#222',
          cursor: canMove && !isClusterChild && !resize.isResizing ? 'move' : 'default',
          display: 'flex',
          flexDirection: 'column',
          fontSize: 15,
          transform: dragTransform,
          userSelect: 'none',
          ...currentPosition,
        },
        ...cardSx,
        isFocusFiltered
          ? {
              bgcolor: 'transparent',
              boxShadow:
                selection.isCurrent || selection.isSelected
                  ? '0 0 0 2px rgba(125, 201, 251, 0.45)'
                  : '0 0 0 1px rgba(15, 42, 57, 0.12)',
              color: 'transparent',
              filter: 'none',
              transition: 'box-shadow 120ms ease',
              '& > *:not([data-resize-handle])': {
                opacity: 0,
              },
            }
          : null,
      ]}
    >
      {children}
      {resize.canResize && <CardResizeHandles getHandleProps={resize.getHandleProps} />}
    </Box>
  );
}

function CardResizeHandles({
  getHandleProps,
}: {
  getHandleProps: ReturnType<typeof useCardResize>['getHandleProps'];
}) {
  return (
    <>
      <ResizeHandle direction="e" getHandleProps={getHandleProps} />
      <ResizeHandle direction="s" getHandleProps={getHandleProps} />
      <ResizeHandle direction="se" getHandleProps={getHandleProps} />
    </>
  );
}

function ResizeHandle({
  direction,
  getHandleProps,
}: {
  direction: CardResizeHandle;
  getHandleProps: ReturnType<typeof useCardResize>['getHandleProps'];
}) {
  return (
    <Box
      component="span"
      data-resize-handle={direction}
      {...getHandleProps(direction)}
      sx={{
        bgcolor: 'transparent',
        display: 'block',
        position: 'absolute',
        touchAction: 'none',
        zIndex: 4,
        ...getResizeHandlePosition(direction),
      }}
    />
  );
}

function getResizeHandlePosition(direction: CardResizeHandle) {
  if (direction === 'e') {
    return {
      bottom: 8,
      cursor: 'ew-resize',
      right: -5,
      top: 8,
      width: 10,
    };
  }

  if (direction === 's') {
    return {
      bottom: -5,
      cursor: 'ns-resize',
      height: 10,
      left: 8,
      right: 8,
    };
  }

  return {
    bottom: -6,
    cursor: 'nwse-resize',
    height: 14,
    right: -6,
    width: 14,
  };
}
